import Head from 'next/head';
import App from 'next/app';
import { ThemeProvider } from '@algoan/deep-dive/theme';
import { Provider } from 'react-redux';
import { EnhancedStore } from '@reduxjs/toolkit';
import { AuthSession, SessionProvider } from '@algoan/authentication';
import { UserDataGetter } from '../modules/user-data-getter';
import { dashboardTheme } from '@algoan/dashboard/theme';
import 'react-virtualized/styles.css';
import 'react-datepicker/dist/react-datepicker.css';
import { anonymizeState, store } from '@algoan/dashboard/store/store';
import { Protected } from '../modules/protected';
import Script from 'next/script';
import { Notification } from '@algoan/dashboard/notification';
import { amplitudeApiKey, apiUrl, enableMockServer, releaseEnv, tgglApiKey } from '@algoan/dashboard/env';
import { LanguageProvider, monitoring } from '@algoan/dashboard/utils';
import { HandleFromCypress } from '@algoan/dashboard/test-utils';
import { ErrorBoundary } from '../error-boundary';
import { TgglProvider, TgglClient } from 'react-tggl-client';
import { ModalProvider } from '../shared-components/global-modal/modal-context';
import { version } from '../../package.json';
import { IntlProvider } from '../utils/intl-provider';

declare global {
  interface Window {
    // for Google Analytics
    gtag?: unknown;
    dataLayer?: unknown[];
    // for Cypress testing
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Cypress: unknown;
    store: EnhancedStore;
    handleFromCypress?: HandleFromCypress;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __ENV: Record<string, string>;
    isRedirectingToKeycloak: boolean | undefined;
    userEmail?: string;
  }
}

if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && !window.Cypress) {
  monitoring.init({
    applicationId: '46733770-ad23-4463-ab02-0fa0461b0cc7',
    clientToken: 'pubd0dd1cb237d1efb101c77a6112e4530e',
    version: version,
    service: 'dashboardwebapp',
    beforeSend: (event) => {
      if (event.type === 'error') {
        // @ts-expect-error Enrich context with state property: https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#enrich-and-control-rum-data
        event.context.state = anonymizeState(store.getState());
      }
      return true;
    },
  });
}

if (process.env.NODE_ENV === 'development') {
  if (enableMockServer) {
    (async function () {
      const { makeServer } = await import('@algoan/dashboard/test-utils');
      makeServer('development');
    })();
  }
}

if (typeof window !== 'undefined' && window.Cypress) {
  await (async function () {
    const { setUpMirageWithCypress } = await import('@algoan/dashboard/test-utils');

    setUpMirageWithCypress();
  })();
}

const featureSwitchClient = new TgglClient(tgglApiKey);
const initialTgglContext = { environment: releaseEnv };

export default class MyApp extends App<{ pageProps: { session: AuthSession } }, Record<string, unknown>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  constructor(props: any, context: any) {
    super(props, context);

    if (
      (typeof window !== 'undefined' && window.Cypress) ||
      (typeof window !== 'undefined' && 'DISABLE_ANALYTICS' in window)
    ) {
      // expose store when run in Cypress
      window.store = store;
    }
  }

  componentDidMount(): void {
    if (typeof window !== 'undefined') {
      import('amplitude-js').then(({ default: amplitude }) => {
        amplitude.getInstance().init(amplitudeApiKey, undefined, {
          optOut: !!window.Cypress || 'DISABLE_ANALYTICS' in window,
          apiEndpoint: `${apiUrl.replace('https://', '')}/amplitude/`,
        });
      });
    }
  }

  render(): JSX.Element {
    return (
      <TgglProvider client={featureSwitchClient} initialContext={initialTgglContext}>
        <SessionProvider session={this.props.pageProps.session}>
          <Provider store={store}>
            <LanguageProvider>
              <IntlProvider>
                <ThemeProvider customTheme={dashboardTheme}>
                  <Head>
                    <title key="title">Dashboard</title>
                    <link rel="manifest" href="/manifest.webmanifest" crossOrigin="use-credentials" />
                    <link rel="icon" href="/images/icons/icon-48x48.png" />
                  </Head>
                  <Notification />
                  <UserDataGetter />
                  <Script src="/__ENV.js" strategy="beforeInteractive" />
                  <ErrorBoundary>
                    <Protected render={() => <ModalProvider>{super.render()}</ModalProvider>} />
                  </ErrorBoundary>
                </ThemeProvider>
              </IntlProvider>
            </LanguageProvider>
          </Provider>
        </SessionProvider>
      </TgglProvider>
    );
  }
}
